// Whether Reports → Cold Chain is reachable, and in which form (sealed-record spec §1, §7). With the module on: the full
// report. With it off, a record already issued must stay verifiable, downloadable and withdrawable: admins, managers and
// Instance Admins whose org holds at least one seal get a seals-only view. The tab (ReportsPage) and the route gate
// (Router) both read this hook, so the two can never disagree.
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useFeatures from '@/hooks/useFeatures';
import { coldChain } from '@/lib/trackizy';
import useCanManageColdChain from './useCanManageColdChain';

const probes = new Map();   // orgId -> Promise<boolean>: one seals lookup per org per page load

/** Does the org hold at least one seal the caller can see? A failure counts as no (the tab stays hidden). */
export function sealsProbe(orgId) {
  if (!probes.has(orgId)) {
    probes.set(orgId, coldChain.seals({ limit: 1 }).then((res) => (res?.seals?.length ?? 0) > 0).catch(() => false));
  }
  return probes.get(orgId);
}

/** Tests only. */
export function resetSealsProbe() { probes.clear(); }

/** { visible, sealsOnly, pending } */
export default function useColdChainReportAccess() {
  const features = useFeatures();
  const canManage = useCanManageColdChain();
  const orgId = useSelector((s) => s.org?.id);
  const needsProbe = !features.temperature && canManage && !!orgId;
  const [probe, setProbe] = useState({ orgId: null, value: false });

  useEffect(() => {
    if (!needsProbe) return undefined;
    let cancelled = false;
    sealsProbe(orgId).then((value) => { if (!cancelled) setProbe({ orgId, value }); });
    return () => { cancelled = true; };
  }, [needsProbe, orgId]);

  if (features.temperature) return { visible: true, sealsOnly: false, pending: false };
  if (!needsProbe) return { visible: false, sealsOnly: false, pending: false };
  const known = probe.orgId === orgId;
  return { visible: known && probe.value, sealsOnly: true, pending: !known };
}
